// Mock Market pool for the Multi-Market Lens Grid (PROTOCOL_SPEC.md §5).
//
// Sandbox-only, client-side. Non-LOCAL lenses render this pool as a
// preview surface — cards use the same §4 CS chrome as real markets,
// but pledges against them are not money-moving. The production cut
// moves this to a server-computed feed.
//
// Scoring reuses §4: `cs = confidenceScore(crowd)`. No independent
// weight table. A market is visible in a lens iff `regions.includes(lens)`.

import { IdentityLevel, IdentityTier } from './identity';
import {
  confidenceScore,
  PulsePledge,
  RegionLens,
} from './socialPulse';

// Mock cards don't traverse the full §6.1 Mint lifecycle — the Mint
// Queue owns the interactive Vouch/Veto flow. But the grid needs to
// *show* stale/terminal states so testers see the lifecycle imagery
// across the whole board, not just on user-minted markets. An undefined
// `status` is the happy path (ACTIVE); the three non-active states mirror
// the Mint state machine.
export type MockMarketStatus = 'STALE' | 'VOIDED_BY_SOVEREIGN' | 'AUTO_REFUNDED';

export interface MarketCard {
  id:         string;
  title:      string;
  regions:    RegionLens[];    // §5 — may contain 1..N lenses
  impliedYes: number;          // [0, 1] — mock CPMM-style P(YES)
  crowd:      PulsePledge[];   // synthetic pledges for CS
  cs:         number;          // confidenceScore(crowd), cached
  volumeOC:   number;          // synthetic notional
  deltaPct:   number;          // synthetic 24h change
  status?:    MockMarketStatus; // undefined ⇒ ACTIVE
  staleAt?:   number;           // ms epoch when STALE transitioned, cleared on recovery
}

// ─── Authoring helpers ──────────────────────────────────────────────────
//
// Mixes are templated so a market's confidence band is mostly
// predictable from its recipe. "VerifiedHeavy" leans citizen/sovereign
// (→ high CS); "Noisy" leans seeker (→ low CS); "Mixed" is balanced.
// This keeps QA tractable — flip to a lens, expect a spread of gold
// and volatile tiles rather than a uniform middle.

interface CrowdRecipe {
  seekers:    [count: number, stakeLo: number, stakeHi: number];
  observers:  [count: number, stakeLo: number, stakeHi: number];
  citizens:   [count: number, stakeLo: number, stakeHi: number];
  sovereigns: [count: number, stakeLo: number, stakeHi: number];
}

function r(lo: number, hi: number): number { return lo + Math.random() * (hi - lo); }

function buildCrowd(recipe: CrowdRecipe): PulsePledge[] {
  const out: PulsePledge[] = [];
  const add = (tier: IdentityTier, spec: CrowdRecipe[keyof CrowdRecipe]): void => {
    const [count, lo, hi] = spec;
    for (let i = 0; i < count; i++) out.push({ tier, stake: Math.round(r(lo, hi)) });
  };
  add(IdentityLevel.SEEKER,    recipe.seekers);
  add(IdentityLevel.OBSERVER,  recipe.observers);
  add(IdentityLevel.CITIZEN,   recipe.citizens);
  add(IdentityLevel.SOVEREIGN, recipe.sovereigns);
  return out;
}

const VERIFIED_HEAVY: CrowdRecipe = {
  seekers:    [3,  10,  60],
  observers:  [4,  40, 180],
  citizens:   [6, 120, 400],
  sovereigns: [2, 200, 600],
};
const MIXED: CrowdRecipe = {
  seekers:    [5,  10,  80],
  observers:  [7,  30, 200],
  citizens:   [3,  80, 300],
  sovereigns: [1, 100, 400],
};
const NOISY: CrowdRecipe = {
  seekers:    [10,  5,  80],
  observers:  [6,  20, 140],
  citizens:   [1,  40, 160],
  sovereigns: [0,   0,   0],
};

let idCounter = 0;
function mintId(region: RegionLens): string {
  idCounter += 1;
  return `mkt_${region.toLowerCase()}_${idCounter}`;
}

function card(
  title:      string,
  regions:    RegionLens[],
  impliedYes: number,
  recipe:     CrowdRecipe,
  deltaPct:   number,
): MarketCard {
  const crowd = buildCrowd(recipe);
  const vol   = crowd.reduce((s, p) => s + p.stake, 0);
  return {
    id:         mintId(regions[0]),
    title,
    regions,
    impliedYes,
    crowd,
    cs:         confidenceScore(crowd),
    volumeOC:   vol,
    deltaPct,
  };
}

// ─── The pool ───────────────────────────────────────────────────────────
//
// Authored once at module load. The sandbox grid is a preview, so a
// fixed deterministic-ish list reads better than a re-rolled pool on
// every lens flip.

const MARKETS: MarketCard[] = [
  // ── GLOBAL ─────────────────────────────────────────────────────────
  card('Will Bitcoin close above $120K by end of Q2?',
       ['GLOBAL'],           0.58, VERIFIED_HEAVY, +4.20),
  card('Will US CPI print hotter than 3.4% next month?',
       ['GLOBAL'],           0.41, MIXED,          -1.80),
  card('Will Brent Crude stay above $90 through May?',
       ['GLOBAL'],           0.63, VERIFIED_HEAVY, +2.10),
  card('Will the Fed cut rates at the June meeting?',
       ['GLOBAL'],           0.22, MIXED,          -3.40),
  card('Will OpenAI announce GPT-6 before August?',
       ['GLOBAL'],           0.18, NOISY,          +7.50),

  // ── ASIA (some multi-lens with GLOBAL) ─────────────────────────────
  card('Will USD/JPY break above 210 before July?',
       ['ASIA', 'GLOBAL'],   0.34, MIXED,          +1.90),
  card('Will Nikkei 225 hit 45,000 by end of Q3?',
       ['ASIA'],             0.47, VERIFIED_HEAVY, +3.60),
  card('Will T1 win the LCK Spring Finals?',
       ['ASIA'],             0.68, MIXED,          +0.80),
  card('Will India beat Australia in the 4th ODI?',
       ['ASIA'],             0.55, NOISY,          -2.20),
  card('Will China PMI stay above 50 for three consecutive months?',
       ['ASIA', 'GLOBAL'],   0.39, VERIFIED_HEAVY, -0.40),

  // ── EUROPE (some multi-lens) ───────────────────────────────────────
  card('Will the ECB cut rates at the April meeting?',
       ['EUROPE', 'GLOBAL'], 0.72, VERIFIED_HEAVY, +2.90),
  card('Will UK CPI come in below 2.5% next print?',
       ['EUROPE'],           0.44, MIXED,          -1.10),
  card('Will Juventus finish top-3 in Serie A?',
       ['EUROPE'],           0.51, MIXED,          +0.60),
  card('Will the DAX close above 22,000 by end of May?',
       ['EUROPE'],           0.29, NOISY,          -4.80),
  card('Will the EU AI Act tier-3 rules activate on schedule?',
       ['EUROPE'],           0.61, VERIFIED_HEAVY, +1.40),
  card('Will North Sea wind capacity grow >15% YoY?',
       ['EUROPE', 'GLOBAL'], 0.37, MIXED,          +0.20),
];

// Seed one STALE at module load so the grid shows stale imagery
// immediately on first paint — otherwise testers would have to wait for
// the 30–60s churn tick to roll the first flip. Picked the NOISY GPT-6
// card since its low-CS band already reads as volatile, which makes the
// STALE overlay narratively consistent ("oracle couldn't resolve it").
(() => {
  const seed = MARKETS.find(m => m.title.startsWith('Will OpenAI announce GPT-6'));
  if (seed) {
    seed.status  = 'STALE';
    seed.staleAt = Date.now();
  }
})();

// Ordering per §5: confidence desc, volume desc as tiebreaker. Stale and
// terminal-state markets sort to the bottom regardless of CS so the
// gold/verified tiles stay at the top of the grid; testers still see the
// STALE/REFUNDED/VOIDED chrome without it dominating the first row.
function statusRank(s: MarketCard['status']): number {
  return s === undefined ? 0 : 1;
}
function byConfidenceThenVolume(a: MarketCard, b: MarketCard): number {
  const rankDelta = statusRank(a.status) - statusRank(b.status);
  if (rankDelta !== 0) return rankDelta;
  if (b.cs !== a.cs) return b.cs - a.cs;
  return b.volumeOC - a.volumeOC;
}

export function listMarketsForLens(lens: RegionLens): MarketCard[] {
  return MARKETS
    .filter(m => m.regions.includes(lens))
    .sort(byConfidenceThenVolume);
}

// v2.2 Lens Radiance — mean absolute divergence across ACTIVE markets
// visible under a lens. Divergence is |confidence − impliedYes|, the
// same [0,1] scalar the Divergence Gauge uses for live markets; the
// Radiance threshold treats > 0.20 as "hot". Terminal states are
// excluded — a voided or auto-refunded card no longer contributes to
// the lens's temperature.
export function averageDivergenceForLens(lens: RegionLens): number {
  const active = MARKETS.filter(m => m.regions.includes(lens) && !m.status);
  if (active.length === 0) return 0;
  const sum = active.reduce((acc, m) => acc + Math.abs(m.cs - m.impliedYes), 0);
  return sum / active.length;
}

// ─── Mock Background Activity (v1.3.2) ──────────────────────────────────
//
// Every 30–60s, 10–20 pledges rain across the pool from a synthetic
// population skewed heavily toward Seekers. This is the "Populating the
// Agora" brief: without churn, `cs` and `volumeOC` freeze at module-load
// values and the Confidence Stars never move. With churn, a noisy market
// can decay from neutral into the red band as the 80% Seeker tail piles
// in, and a verified-heavy market can drift up into the gold band when
// a Citizen injects weight.
//
// Distribution per brief v1.4:
//   80% Seekers     (noise  — CW 0.25) → triggers UNVERIFIED / VOLATILE
//   15% Observers   (medium — CW 1.0)  → the standard wallet-connected majority
//    5% Citizens    (signal — CW 2.0)  → pushes toward CITIZEN VERIFIED
//   Sovereigns stay out of background churn; their weight is reserved
//   for live protocol moments rather than sandbox noise.

const ACTIVITY_TICK_MS_MIN = 30_000;
const ACTIVITY_TICK_MS_MAX = 60_000;
const PLEDGES_PER_TICK     = [10, 20]; // total pledges per tick, scattered across the pool
const CROWD_CAP            = 60;       // shift oldest when exceeded so volume doesn't balloon

// Stale lifecycle (§6.1 visual mirror for the mock pool).
// Keeping the population near 2% overall: one seed at boot plus a
// per-tick roll on a random ACTIVE card. Terminal states (VOIDED,
// AUTO_REFUNDED) visibly linger for a couple of ticks before the card
// recovers to ACTIVE so the grid doesn't monotonically fill with
// terminal chrome.
const STALE_FLIP_PROBABILITY   = 0.02;   // per tick, per pool
const STALE_TO_TERMINAL_AFTER  = 30_000; // ms a STALE card waits before rolling terminal
const TERMINAL_RECOVER_AFTER   = 20_000; // ms a VOIDED/REFUNDED lingers before reverting
const VOIDED_VS_REFUNDED_RATIO = 0.2;    // ~20% Sovereign Veto, ~80% Auto-Refund (matches §6.1 bias)
const MAX_NON_ACTIVE           = 2;      // soft ceiling so the pool stays visually ACTIVE-dominant

function rollActivityTier(): IdentityTier {
  const r = Math.random();
  if (r < 0.80) return IdentityLevel.SEEKER;
  if (r < 0.95) return IdentityLevel.OBSERVER;
  return IdentityLevel.CITIZEN;
}

// Subscriber list — React components call `subscribeMarketActivity` in
// an effect and re-read the pool on each notification. We start the
// tick lazily on the first subscribe so modules that import the pool
// without rendering (e.g. tests) don't leak a timer.

type Listener = () => void;
const listeners: Set<Listener> = new Set();
let tickHandle: ReturnType<typeof setTimeout> | null = null;

function churnOnce(): void {
  // Scatter PLEDGES_PER_TICK individual pledges across random markets.
  // Some markets may receive several, others none — which mirrors the
  // organic feel of a live feed better than a uniform "5 markets × 2
  // pledges" grid. Stale / terminal markets are excluded: pledges are
  // frozen while the §6.1 escape valve runs.
  const total   = Math.floor(r(PLEDGES_PER_TICK[0], PLEDGES_PER_TICK[1] + 1));
  const touched = new Set<number>();
  const activeIdxs: number[] = [];
  MARKETS.forEach((m, i) => { if (m.status === undefined) activeIdxs.push(i); });
  for (let i = 0; i < total; i++) {
    if (activeIdxs.length === 0) break;
    const idx  = activeIdxs[Math.floor(Math.random() * activeIdxs.length)];
    const m    = MARKETS[idx];
    const tier = rollActivityTier();
    // Seekers pledge small, Observers medium, Citizens large — roughly
    // aligned with the authored recipes above so injected pledges feel
    // contiguous with the seed crowd rather than obviously synthetic.
    const stake =
      tier === IdentityLevel.SEEKER   ? Math.round(r( 5,  80)) :
      tier === IdentityLevel.OBSERVER ? Math.round(r(20, 160)) :
                                        Math.round(r(80, 320));
    m.crowd.push({ tier, stake });
    while (m.crowd.length > CROWD_CAP) m.crowd.shift();
    touched.add(idx);
  }

  // Recompute cached metrics on the markets that actually changed.
  // `forEach` rather than `for-of` to stay ES5-safe without enabling
  // `downlevelIteration` — CRA's default target doesn't iterate Sets.
  touched.forEach(idx => {
    const m = MARKETS[idx];
    m.cs       = confidenceScore(m.crowd);
    m.volumeOC = m.crowd.reduce((s, p) => s + p.stake, 0);
    // Small random walk on the 24h delta so the terminal "feels alive"
    // without drifting off a sensible range.
    m.deltaPct = +(Math.max(-15, Math.min(15, m.deltaPct + r(-0.8, 0.8)))).toFixed(2);
  });

  advanceStaleLifecycle();

  listeners.forEach(fn => fn());
}

// Walks the pool once per churn tick and moves stale/terminal markets
// along the §6.1 visual arc. Kept separate from pledge churn so the
// lifecycle rules are legible in one place:
//   ACTIVE   → STALE                 (small random probability, capped)
//   STALE    → VOIDED | AUTO_REFUNDED (after STALE_TO_TERMINAL_AFTER ms)
//   TERMINAL → ACTIVE                 (after TERMINAL_RECOVER_AFTER ms)
function advanceStaleLifecycle(): void {
  const now = Date.now();

  // 1. Progress existing non-active markets.
  for (const m of MARKETS) {
    if (m.status === 'STALE' && m.staleAt !== undefined && now - m.staleAt >= STALE_TO_TERMINAL_AFTER) {
      m.status = Math.random() < VOIDED_VS_REFUNDED_RATIO ? 'VOIDED_BY_SOVEREIGN' : 'AUTO_REFUNDED';
      // Reuse staleAt as the "terminal-entered-at" timestamp so the
      // recovery timer is keyed off a single field rather than
      // duplicating state.
      m.staleAt = now;
      continue;
    }
    if ((m.status === 'VOIDED_BY_SOVEREIGN' || m.status === 'AUTO_REFUNDED')
        && m.staleAt !== undefined
        && now - m.staleAt >= TERMINAL_RECOVER_AFTER) {
      m.status  = undefined;
      m.staleAt = undefined;
    }
  }

  // 2. Maybe flip an ACTIVE to STALE, subject to the population cap.
  const nonActiveCount = MARKETS.filter(m => m.status !== undefined).length;
  if (nonActiveCount < MAX_NON_ACTIVE && Math.random() < STALE_FLIP_PROBABILITY) {
    const actives = MARKETS.filter(m => m.status === undefined);
    if (actives.length > 0) {
      const victim = actives[Math.floor(Math.random() * actives.length)];
      victim.status  = 'STALE';
      victim.staleAt = now;
    }
  }
}

function scheduleNextTick(): void {
  // Each tick picks its own random delay in [MIN, MAX) so the cadence
  // is organic rather than metronomic. setTimeout + self-reschedule is
  // the simplest way to vary the interval per cycle.
  const delay = r(ACTIVITY_TICK_MS_MIN, ACTIVITY_TICK_MS_MAX);
  tickHandle = setTimeout(() => {
    churnOnce();
    scheduleNextTick();
  }, delay);
}

function ensureTickerRunning(): void {
  if (tickHandle !== null) return;
  if (typeof window === 'undefined') return; // SSR / test — no-op
  scheduleNextTick();
}

export function subscribeMarketActivity(fn: Listener): () => void {
  listeners.add(fn);
  ensureTickerRunning();
  return () => {
    listeners.delete(fn);
    // Intentionally keep the ticker running even when listener count
    // hits zero; the pool keeps evolving so a lens flip back into view
    // shows a board that *moved*, not a frozen snapshot from the last
    // time it was visible.
  };
}
